/* Relative time as a whole localized phrase — "5 хвилин тому", "2 hours ago", "вчора".
 *
 * `Intl.RelativeTimeFormat`, not dayjs' `relativeTime` plugin: no dayjs locale is loaded anywhere in
 * this app (`util/dayjs.js`), and dayjs ships no Armenian one at all. `Intl` also returns the whole
 * phrase per locale, so no noun is ever inflected against a number by hand (spec/04).
 *
 * The largest unit that is not zero, capped at days — the same reading `/photo-review` gives a
 * visit's start. A value in the future (clock skew) reads as "now" rather than "in 3 minutes".
 */
import { localeFor } from "../module/merchandiser/route/dateUtils";

/* One formatter per locale rather than one per call. */
const FORMATS = new Map();

/* ⚠ A browser built without a locale's data does not throw — `Intl` silently formats in the
   BROWSER's default language instead, so a Georgian account on such a build read "6 днів тому" on a
   Ukrainian machine (seen in the snap build of Brave, which carries `uk` and `en` only). Falling
   back to English explicitly is what `translate()` does for a missing string. */
const formatFor = lang => {
  const wanted = localeFor(lang);
  if (!FORMATS.has(wanted)) {
    const locale = Intl.RelativeTimeFormat.supportedLocalesOf(wanted).length ? wanted : "en-GB";
    FORMATS.set(wanted, new Intl.RelativeTimeFormat(locale, { numeric: "auto" }));
  }
  return FORMATS.get(wanted);
};

/** `value` is anything `Date` parses (an ISO datetime from the API). Empty / unparseable → "". */
export function relativeTime(value, lang, now = Date.now()) {
  if (!value) return "";
  const time = new Date(value).getTime();
  if (Number.isNaN(time)) return "";
  const minutes = Math.max(Math.round((now - time) / 60000), 0);
  if (minutes < 60) return formatFor(lang).format(-minutes, "minute");
  if (minutes < 1440) return formatFor(lang).format(-Math.round(minutes / 60), "hour");
  return formatFor(lang).format(-Math.round(minutes / 1440), "day");
}
